import { useEffect, useState } from "react";
import { supAPI } from "./supApi";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";
import { EmptyState } from "./SupplyItems";

export default function SupplyStores() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => supAPI.stores().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="sup-stores">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Stores</h2>
          <p className="text-[12.5px] text-slate-500">Physical stock locations (Main Store, F&B Store, Housekeeping, etc.).</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="sup-store-new"
                className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Add Store
        </button>
      </div>

      {!rows.length ? <EmptyState msg="No stores yet. Add at least one to start tracking stock."/> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(s => (
            <div key={s.id} data-testid={`sup-store-card-${s.id}`}
                 className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11.5px] mono text-slate-400">{s.code}</div>
              <div className="font-display text-[15px] font-semibold text-hocp-navy mt-1">{s.name}</div>
              <div className="text-[11.5px] text-slate-500 mt-2">
                <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md">{s.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <StoreForm onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>
          </div>
        </div>
      )}
    </div>
  );
}

function StoreForm({ onClose, onCreated }) {
  const [f, setF] = useState({ code: "", name: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.code || !f.name) { toast.error("Code and name required"); return; }
    setSaving(true);
    try { await supAPI.createStore(f); toast.success("Store created"); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Store</h3>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
      </div>
      <div className="p-5 space-y-3">
        <label className="block">
          <div className="text-[11.5px] text-slate-500 mb-1 font-medium">Code</div>
          <input value={f.code} onChange={e=>setF({...f,code:e.target.value.toUpperCase()})} data-testid="new-store-code" placeholder="e.g. MAIN, F&B, HK"
                 className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]"/>
        </label>
        <label className="block">
          <div className="text-[11.5px] text-slate-500 mb-1 font-medium">Name</div>
          <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="new-store-name"
                 className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]"/>
        </label>
      </div>
      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
        <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="new-store-submit"
                className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create"}
        </button>
      </div>
    </>
  );
}
